import React from 'react';
import { Link } from 'react-router-dom';
import { Wind, Shield, Lock, Eye, UserCheck, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-dark-primary">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-primary/80 backdrop-blur-lg border-b border-gold/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Wind className="w-8 h-8 text-gold" />
            <span className="text-2xl font-bold text-gold">MADTOYS</span>
          </Link>
          <Link to="/" className="text-gray-300 hover:text-gold transition-colors">
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <h1 className="text-5xl font-bold text-white mb-6">Privacy Policy</h1>
          <p className="text-gray-400 mb-4">Last updated: December 2025</p>
          <p className="text-xl text-gray-400 mb-12">
            At MADTOYS, we are committed to protecting your privacy and ensuring the security of your personal information.
          </p>

          <div className="space-y-8">
            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <FileText className="w-6 h-6 text-gold" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mt-2">Information We Collect</h2>
                </div>
                <div className="space-y-4 text-gray-300">
                  <div>
                    <h3 className="text-lg font-semibold text-gold mb-2">Personal Information</h3>
                    <p>When you make a purchase or create an account, we collect:</p>
                    <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                      <li>Name and contact information (email, phone number)</li>
                      <li>Shipping and billing address</li>
                      <li>Payment information (processed securely through our payment partners)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gold mb-2">Automatically Collected Information</h3>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Browser type and device information</li>
                      <li>IP address and location data</li>
                      <li>Pages visited and time spent on our website</li>
                      <li>Cookies and similar tracking technologies</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <Eye className="w-6 h-6 text-gold" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mt-2">How We Use Your Information</h2>
                </div>
                <p className="text-gray-300 mb-3">We use the collected information for the following purposes:</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span>Processing and fulfilling your orders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span>Communicating with you about your orders and inquiries</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span>Improving our website and customer service</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span>Sending promotional emails (only if you opt-in)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span>Detecting and preventing fraud</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span>Complying with legal obligations</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <Lock className="w-6 h-6 text-gold" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mt-2">Data Security</h2>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  We implement industry-standard security measures to protect your personal information from unauthorized 
                  access, disclosure, or misuse. This includes SSL encryption for data transmission, secure payment 
                  processing through trusted third-party providers, and regular security audits. However, no method of 
                  transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <Shield className="w-6 h-6 text-gold" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mt-2">Information Sharing</h2>
                </div>
                <p className="text-gray-300 mb-3">We do not sell your personal information. We may share your data with:</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span><strong>Service Providers:</strong> Shipping partners, payment processors, and email service providers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span><strong>Legal Authorities:</strong> When required by law or to protect our rights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-gold/10 rounded-lg">
                    <UserCheck className="w-6 h-6 text-gold" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mt-2">Your Rights</h2>
                </div>
                <p className="text-gray-300 mb-3">You have the following rights regarding your personal data:</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span><strong>Access:</strong> Request a copy of the personal information we hold about you</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span><strong>Correction:</strong> Request correction of inaccurate or incomplete information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span><strong>Deletion:</strong> Request deletion of your personal information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gold mt-1">•</span>
                    <span><strong>Data Portability:</strong> Request transfer of your data to another service</span>
                  </li>
                </ul>
                <p className="text-gray-300 mt-4">
                  To exercise these rights, please contact us at <span className="text-gold">info@madtoys.in</span>
                </p>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Cookies</h2>
                <p className="text-gray-300 leading-relaxed">
                  We use cookies and similar technologies to enhance your browsing experience, analyze website traffic, 
                  and personalize content. You can control cookie preferences through your browser settings. Disabling 
                  cookies may affect the functionality of our website.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Third-Party Links</h2>
                <p className="text-gray-300 leading-relaxed">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices 
                  of these external sites. We encourage you to review their privacy policies before providing any personal information.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Children's Privacy</h2>
                <p className="text-gray-300 leading-relaxed">
                  Our website is not intended for children under the age of 18. We do not knowingly collect personal 
                  information from children. If you believe we have inadvertently collected such information, please 
                  contact us immediately.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Changes to This Policy</h2>
                <p className="text-gray-300 leading-relaxed">
                  We may update this privacy policy from time to time. We will notify you of any significant changes by 
                  posting the new policy on this page with an updated "Last updated" date. We encourage you to review 
                  this policy periodically.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gold/10 border-gold/30">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gold mb-4">Contact Us</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  If you have any questions or concerns about this privacy policy or our data practices, please contact us:
                </p>
                <div className="text-gray-300 space-y-2">
                  <p><strong className="text-gold">Email:</strong> info@madtoys.in</p>
                  <p><strong className="text-gold">Phone:</strong> +91-9137044474</p>
                  <p><strong className="text-gold">Bulk Orders:</strong> +91-8237530525</p>
                  <p><strong className="text-gold">Website:</strong> madtoys.in</p>
                  <p><strong className="text-gold">Support Hours:</strong> 10 AM - 6 PM IST (Monday to Saturday)</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
